import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useDeleteScanRequestMutation } from '../../api/scan-requests-api';
import { scanRequestAlertId, scanRequestDeleteModalId } from '../../constants/constants';
import { selectTempScanRequestId, setTempScanRequestId } from '../../store/scan-requests-slice';

const ScanRequestDeleteModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['scan-requests', 'common']);
  const dispatch = useDispatch();
  const activeScanRequestId = useAppSelector(selectTempScanRequestId);
  const [deleteScanRequest, { isLoading }] = useDeleteScanRequestMutation();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();

  const closeFormModal = () => {
    dispatch(closeModal(scanRequestDeleteModalId));
    dispatch(setTempScanRequestId(null));
    resetConfirmationError();
  };

  const setScanRequestDeleteAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('scanRequestDeleteModal.message.fileDeleted'),
      }),
    );
  };

  const onModalSubmit = async () => {
    resetConfirmationError();

    if (activeScanRequestId) {
      deleteScanRequest(activeScanRequestId)
        .unwrap()
        .then(() => {
          closeFormModal();
          setScanRequestDeleteAlertMessage(scanRequestAlertId);
          setScanRequestDeleteAlertMessage(requestPreviewAlertId);
        })
        .catch(() => addConfirmationError(t('errors.deleteFailed', { ns: 'common' })));
    }
  };

  return (
    <Modal
      title={t('scanRequestDeleteModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('action.delete', { ns: 'common' })}
      onOk={onModalSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <ModalConfirmationContent
        confirmDeleteText={t('signScanRequest.hint.deleteWarning', { ns: 'common' })}
        confirmationError={confirmationError}
      />
    </Modal>
  );
};

const ScanRequestDeleteModal = (): JSX.Element => {
  return <LazyModal modalId={scanRequestDeleteModalId} Component={ScanRequestDeleteModalContent} />;
};

export default ScanRequestDeleteModal;
